import React from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-hot-toast';
import { buildTemplateComposerUpdate } from './templateUpdate';
import { buildRecipientContactOptions, normalizeRecipientContact } from './recipientOptions';
import EmailViewer, { getEmailLayoutLabel, normalizeEmailViewerLayout } from '../EmailViewer';

const dedupeContacts = (contacts = []) => buildRecipientContactOptions(contacts);

const toEmailIds = (contacts = []) => new Set(contacts.map((contact) => contact?.id).filter(Boolean));
const normalizeText = (value) => String(value || '').trim();
const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
const getContactBadge = (contact = {}) =>
  normalizeText(contact?.badge || contact?.label);
const getContactBaseLabel = (contact = {}) => {
  const email = normalizeText(contact?.email || contact?.id);
  const name = normalizeText(contact?.name || `${contact?.firstName || ''} ${contact?.lastName || ''}`);
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} (${email})`;
  }
  return email || name || '';
};
const displayContactLabel = (contact = {}) => {
  const base = getContactBaseLabel(contact);
  const badge = getContactBadge(contact);
  if (!badge) return base;
  return `${base} (${badge})`;
};
const contactInitials = (contact = {}) => {
  const name = normalizeText(contact?.name || [contact?.firstName || contact?.first_name, contact?.lastName || contact?.last_name].filter(Boolean).join(' '));
  const email = normalizeText(contact?.email || contact?.id);
  const parts = name && name.toLowerCase() !== email.toLowerCase()
    ? name.split(/\s+/).filter(Boolean)
    : [];
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (email.split('@')[0] || '?').slice(0, 2).toUpperCase();
};
const contactAvatar = (contact = {}, size = 25) => (
  <Avatar
    src={contact?.profilePicture || contact?.avatarUrl || contact?.avatar || contact?.photoUrl || undefined}
    sx={{ width: size, height: size, fontSize: size * 0.39, fontWeight: 700, bgcolor: '#dbeafe', color: '#1e40af', flexShrink: 0 }}
  >
    {contactInitials(contact)}
  </Avatar>
);
const renderContactChip = (option, tagProps) => {
  const badge = getContactBadge(option);
  const baseLabel = getContactBaseLabel(option);
  return (
    <Chip
      {...tagProps}
      avatar={contactAvatar(option, 25)}
      label={badge ? `${baseLabel} • ${badge}` : baseLabel}
      size="medium"
      sx={{
        maxWidth: '100%',
        height: 34,
        borderRadius: '9px',
        bgcolor: '#f1f5f9',
        border: '1px solid #dbe3ef',
        color: '#273449',
        fontWeight: 500,
        '& .MuiChip-avatar': { marginRight: '5px' },
        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', px: 0.8 },
        '& .MuiChip-deleteIcon': { color: '#8795a9', '&:hover': { color: '#334155' } },
      }}
    />
  );
};
const renderContactOption = (props, option) => {
  const badge = getContactBadge(option);
  const email = normalizeText(option?.email || option?.id);
  const name = normalizeText(option?.name || [option?.firstName, option?.lastName].filter(Boolean).join(' '));
  const distinctName = name && name.toLowerCase() !== email.toLowerCase();
  return (
    <li {...props}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, width: '100%', py: 0.45 }}>
        {contactAvatar(option, 36)}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {distinctName ? name : email}
          </Typography>
          {distinctName && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{email}</Typography>}
        </Box>
        {badge && <Chip size="small" label={badge} variant={option?.badgeVariant || 'outlined'} color={option?.badgeColor || 'default'} />}
      </Box>
    </li>
  );
};
const normalizeContactInput = normalizeRecipientContact;

const defaultQuickActions = {
  bccMe: false,
  bccOrderRep: false,
  bccClientRep: false,
  requestReadReceipt: false,
  attachPdf: false,
  attachDashboard: false,
};

const defaultLayoutOptions = [
  { value: 'plain', label: 'Basic email (no template)' },
  { value: 'elite', label: 'Elite template' },
];

const resolveSmartCodes = (input, codeMap) => {
  if (typeof input !== 'string' || !input.includes('{')) return input;
  return input.replace(/\{([a-zA-Z0-9_]+)\}/g, (full, key) => {
    const value = codeMap?.[key];
    return value == null || value === '' ? full : String(value);
  });
};

const toRecipientSummary = (recipients = []) =>
  recipients
    .map((contact) => displayContactLabel(contact))
    .filter(Boolean)
    .join(', ');

const EmailPreviewPanel = ({
  bodyHtml,
  layout,
  subject,
  fromName = '',
  fromEmail = '',
  showTemplatePreviewRow = true,
  to = [],
  cc = [],
  bcc = [],
  attachments = [],
}) => {
  const normalizedLayout = normalizeEmailViewerLayout(layout);
  const metaRows = [
    showTemplatePreviewRow ? { label: 'Template', value: getEmailLayoutLabel(normalizedLayout) } : null,
    { label: 'To', value: toRecipientSummary(to) },
    { label: 'CC', value: toRecipientSummary(cc) },
    { label: 'BCC', value: toRecipientSummary(bcc) },
  ].filter((row) => row && normalizeText(row.value));

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Preview
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
        This is a preview of the email content before sending.
      </Typography>
      <Box
        sx={{
          maxHeight: 440,
          overflow: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
          bgcolor: '#f8fafc',
        }}
      >
        <EmailViewer
          layout={normalizedLayout}
          subject={subject}
          fromName={fromName}
          fromEmail={fromEmail}
          to={to}
          cc={cc}
          bcc={bcc}
          htmlBody={bodyHtml}
          attachments={attachments}
          extraHeaderRows={metaRows.filter((row) => row.label !== 'To' && row.label !== 'CC' && row.label !== 'BCC')}
        />
      </Box>
    </Box>
  );
};

const EmailComposer = ({
  contacts = [],
  templates = [],
  value,
  onChange,
  attachments = [],
  onAttachmentsChange,
  showRecipients = true,
  showAttachments = true,
  showQuickActions = true,
  showLayout = true,
  docId = '',
  smartCodeValues = {},
  quickActionRecipients = {
    me: [],
    orderReps: [],
    clientReps: [],
  },
  showFromField = false,
  fromValue = '',
  fromOptions = [],
  fromLabel = 'From',
  fromHelperText = '',
  fromName = '',
  fromEmail = '',
  showTemplatePreviewRow = true,
  additionalOptions = [],
  showTemplateSelect = true,
  subjectReadOnly = false,
  preserveSubjectOnTemplateChange = false,
  previewBodyHtml,
}) => {
  const safeValue = value || {
    to: [],
    cc: [],
    bcc: [],
    subject: '',
    body: '',
    templateId: '',
    layout: 'plain',
    from: '',
    quickActions: defaultQuickActions,
  };
  const normalizeContactList = (list = []) => {
    const directory = new Map(availableContacts.map((contact) => [contact.email.toLowerCase(), contact]));
    const normalized = (list || []).map((entry) => {
      const contact = normalizeContactInput(entry);
      if (!contact) return null;
      const match = directory.get(String(contact.id).toLowerCase());
      return match ? { ...contact, ...match, id: contact.id, email: contact.id, name: contact.name === contact.id ? (match.name || contact.name) : contact.name } : contact;
    }).filter(Boolean);
    return dedupeContacts(normalized);
  };

  const availableContacts = React.useMemo(() => buildRecipientContactOptions(contacts), [contacts]);
  const quickActions = { ...defaultQuickActions, ...(safeValue.quickActions || {}) };
  const normalizedFromOptions = React.useMemo(
    () => dedupeContacts((fromOptions || []).map(normalizeContactInput).filter(Boolean)),
    [fromOptions]
  );
  const resolvedFromValue = React.useMemo(() => {
    const fromRaw = normalizeText(safeValue?.from || fromValue);
    if (!fromRaw) return null;
    const match = normalizedFromOptions.find((opt) => String(opt.id).toLowerCase() === fromRaw.toLowerCase());
    if (match) return match;
    if (looksLikeEmail(fromRaw)) return { id: fromRaw, email: fromRaw, name: fromRaw };
    return null;
  }, [safeValue?.from, fromValue, normalizedFromOptions]);
  const [bodyMode, setBodyMode] = React.useState('visual');
  const [showPreview, setShowPreview] = React.useState(true);
  const visualBodyRef = React.useRef(null);
  const subjectInputRef = React.useRef(null);
  const primaryContact = safeValue?.to?.[0] || {};
  const contactName = String(
    primaryContact?.name ||
    [primaryContact?.firstName, primaryContact?.lastName].filter(Boolean).join(' ')
  ).trim();
  const inferredFirstName = String(primaryContact?.firstName || contactName.split(' ')[0] || '').trim();
  const inferredLastName = String(primaryContact?.lastName || contactName.split(' ').slice(1).join(' ') || '').trim();
  const inferredEmail = String(primaryContact?.email || primaryContact?.id || '').trim();
  const smartCodeMap = React.useMemo(
    () => ({
      contactFirstName: inferredFirstName,
      contactLastName: inferredLastName,
      contactEmail: inferredEmail,
      docId: docId || safeValue?.docId || '',
      ...smartCodeValues,
    }),
    [inferredFirstName, inferredLastName, inferredEmail, docId, safeValue?.docId, smartCodeValues]
  );

  React.useEffect(() => {
    if (bodyMode !== 'visual') return;
    if (visualBodyRef.current && visualBodyRef.current.innerHTML !== safeValue.body) {
      visualBodyRef.current.innerHTML = safeValue.body || '';
    }
  }, [bodyMode, safeValue.body]);

  const updateValue = (next, { resolveSubject = true } = {}) => {
    const merged = { ...safeValue, ...next };
    onChange?.({
      ...merged,
      subject: resolveSubject ? resolveSmartCodes(merged.subject, smartCodeMap) : merged.subject,
      body: resolveSmartCodes(merged.body, smartCodeMap),
    });
  };
  const handleRecipientsChange = (field, nextList) => {
    updateValue({ [field]: normalizeContactList(nextList) });
  };

  const applyBodyCommand = (command, value = null) => {
    if (typeof document === 'undefined') return;
    document.execCommand(command, false, value);
    if (visualBodyRef.current) {
      updateValue({ body: visualBodyRef.current.innerHTML });
    }
  };
  const insertSmartCodeValue = (key) => {
    const value = smartCodeMap?.[key];
    if (value == null || value === '') return;
    applyBodyCommand('insertText', String(value));
  };
  const insertSubjectValue = (value) => {
    if (subjectReadOnly) return;
    if (value == null || value === '') return;
    const insertText = String(value);
    const inputEl = subjectInputRef.current;
    const current = safeValue.subject || '';

    if (!inputEl || typeof inputEl.selectionStart !== 'number') {
      updateValue({ subject: `${current}${insertText}` });
      return;
    }

    const start = inputEl.selectionStart;
    const end = inputEl.selectionEnd;
    const next = `${current.slice(0, start)}${insertText}${current.slice(end)}`;
    updateValue({ subject: next });

    requestAnimationFrame(() => {
      inputEl.focus();
      const pos = start + insertText.length;
      inputEl.setSelectionRange(pos, pos);
    });
  };

  const handleQuickActionChange = (key, checked) => {
    const nextQuickActions = { ...quickActions, [key]: checked };
    const recipientsByKey = {
      bccMe: quickActionRecipients.me || [],
      bccOrderRep: quickActionRecipients.orderReps || [],
      bccClientRep: quickActionRecipients.clientReps || [],
    };

    const recipients = recipientsByKey[key] || [];
    let nextBcc = safeValue.bcc || [];

    if (checked && (key === 'bccOrderRep' || key === 'bccClientRep') && !recipients.length) {
      const label = key === 'bccOrderRep' ? 'order-rep' : 'client-rep';
      toast.error(`No user with role "${label}" found on order.`);
    }

    if (recipients.length) {
      const recipientIds = toEmailIds(recipients);
      if (checked) {
        nextBcc = dedupeContacts([...nextBcc, ...recipients]);
      } else {
        const stillRequired = new Set();
        if (nextQuickActions.bccMe) {
          quickActionRecipients.me?.forEach((contact) => contact?.id && stillRequired.add(contact.id));
        }
        if (nextQuickActions.bccOrderRep) {
          quickActionRecipients.orderReps?.forEach((contact) => contact?.id && stillRequired.add(contact.id));
        }
        if (nextQuickActions.bccClientRep) {
          quickActionRecipients.clientReps?.forEach((contact) => contact?.id && stillRequired.add(contact.id));
        }

        nextBcc = (nextBcc || []).filter((contact) => {
          if (!contact?.id) return false;
          if (!recipientIds.has(contact.id)) return true;
          return stillRequired.has(contact.id);
        });
      }
    }

    updateValue({ quickActions: nextQuickActions, bcc: nextBcc });
  };

  const handleTemplateChange = (event) => {
    const templateId = event.target.value;
    const template = templates.find((item) => String(item.id) === String(templateId));
    updateValue(buildTemplateComposerUpdate({
      templateId,
      template,
      currentSubject: safeValue.subject,
      currentBody: safeValue.body,
      subjectReadOnly,
      preserveSubjectOnTemplateChange,
    }), { resolveSubject: !preserveSubjectOnTemplateChange });
  };

  const handleFilesChange = (event) => {
    const files = Array.from(event.target.files || []);
    onAttachmentsChange?.(files);
    updateValue({ attachments: files });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Email details
      </Typography>

      {showFromField && (
        normalizedFromOptions.length > 0 ? (
          <Autocomplete
            options={normalizedFromOptions}
            value={resolvedFromValue}
            onChange={(_, next) => updateValue({ from: next?.id || '' })}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : displayContactLabel(opt))}
            isOptionEqualToValue={(opt, val) => String(opt?.id || '').toLowerCase() === String(val?.id || '').toLowerCase()}
            renderInput={(params) => (
              <TextField
                {...params}
                label={fromLabel}
                size="small"
                helperText={fromHelperText}
              />
            )}
          />
        ) : (
          <TextField
            label={fromLabel}
            size="small"
            value={safeValue?.from || fromValue}
            fullWidth
            InputProps={{ readOnly: true }}
            helperText={fromHelperText}
          />
        )
      )}

      {showRecipients && (
        <>
          <Autocomplete
            multiple
            freeSolo
            options={availableContacts}
            value={normalizeContactList(safeValue.to)}
            onChange={(_, val) => handleRecipientsChange('to', val)}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : displayContactLabel(opt))}
            isOptionEqualToValue={(opt, val) => String(opt?.id || '').toLowerCase() === String(val?.id || '').toLowerCase()}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <React.Fragment key={`${option?.id || option}-${index}`}>
                {renderContactChip(option, getTagProps({ index }))}
              </React.Fragment>
            ))}
            renderOption={renderContactOption}
            renderInput={(params) => (
              <TextField {...params} label="To" size="small" />
            )}
          />

          <Autocomplete
            multiple
            freeSolo
            options={availableContacts}
            value={normalizeContactList(safeValue.cc)}
            onChange={(_, val) => handleRecipientsChange('cc', val)}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : displayContactLabel(opt))}
            isOptionEqualToValue={(opt, val) => String(opt?.id || '').toLowerCase() === String(val?.id || '').toLowerCase()}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <React.Fragment key={`${option?.id || option}-${index}`}>
                {renderContactChip(option, getTagProps({ index }))}
              </React.Fragment>
            ))}
            renderOption={renderContactOption}
            renderInput={(params) => (
              <TextField {...params} label="CC" size="small" />
            )}
          />

          <Autocomplete
            multiple
            freeSolo
            options={availableContacts}
            value={normalizeContactList(safeValue.bcc)}
            onChange={(_, val) => handleRecipientsChange('bcc', val)}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : displayContactLabel(opt))}
            isOptionEqualToValue={(opt, val) => String(opt?.id || '').toLowerCase() === String(val?.id || '').toLowerCase()}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <React.Fragment key={`${option?.id || option}-${index}`}>
                {renderContactChip(option, getTagProps({ index }))}
              </React.Fragment>
            ))}
            renderOption={renderContactOption}
            renderInput={(params) => (
              <TextField {...params} label="BCC" size="small" />
            )}
          />
        </>
      )}

      {showQuickActions && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Quick actions
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={quickActions.bccMe}
                  onChange={(_, checked) => handleQuickActionChange('bccMe', checked)}
                />
              )}
              label="BCC Me"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={quickActions.bccOrderRep}
                  onChange={(_, checked) => handleQuickActionChange('bccOrderRep', checked)}
                />
              )}
              label="BCC Order Rep"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={quickActions.bccClientRep}
                  onChange={(_, checked) => handleQuickActionChange('bccClientRep', checked)}
                />
              )}
              label="BCC Client Rep"
            />
            <FormControlLabel
              control={<Checkbox checked={quickActions.requestReadReceipt} disabled />}
              label="Request read receipt"
            />
            <FormControlLabel
              control={<Checkbox checked={quickActions.attachPdf} disabled />}
              label="Attach PDF file"
            />
            <FormControlLabel
              control={<Checkbox checked={quickActions.attachDashboard} disabled />}
              label="Attach customer dashboard link"
            />
          </FormGroup>
        </Box>
      )}

      {additionalOptions.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Options
          </Typography>
          <FormGroup>
            {additionalOptions.map((opt) => (
              <FormControlLabel
                key={opt.key}
                control={(
                  <Checkbox
                    checked={!!opt.checked}
                    onChange={(_, checked) => opt.onChange?.(checked)}
                  />
                )}
                label={opt.label}
              />
            ))}
          </FormGroup>
        </Box>
      )}

      {showAttachments && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Attached file(s)
          </Typography>
          <TextField
            type="file"
            size="small"
            inputProps={{ multiple: true }}
            onChange={handleFilesChange}
          />
          {attachments.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {attachments.map((file, index) => (
                <Chip
                  key={`${file.name}-${index}`}
                  label={file.name}
                  onDelete={() =>
                    onAttachmentsChange?.(attachments.filter((_, fileIndex) => fileIndex !== index))
                  }
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {showTemplateSelect && (
        <TextField
          label="Template"
          select
          size="small"
          value={safeValue.templateId || ''}
          onChange={handleTemplateChange}
          helperText={templates.length ? "Select a template to auto-fill subject and body." : "No templates available."}
        >
          <MenuItem value="">None</MenuItem>
          {templates.map((template) => (
            <MenuItem key={template.id} value={template.id}>
              {template.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        label="Subject"
        size="small"
        value={safeValue.subject}
        inputRef={subjectInputRef}
        onChange={(event) => updateValue({ subject: event.target.value })}
        InputProps={{ readOnly: subjectReadOnly }}
        helperText={subjectReadOnly
          ? "This subject is fixed for this email."
          : "Use the smart-code insert buttons below (no manual token typing needed)."}
      />
      {!subjectReadOnly && (
        <Stack direction="row" spacing={1} sx={{ mt: -1, mb: 0.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => insertSubjectValue(smartCodeMap?.contactFirstName)}
            disabled={!smartCodeMap?.contactFirstName}
          >
            First name
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => insertSubjectValue(smartCodeMap?.contactLastName)}
            disabled={!smartCodeMap?.contactLastName}
          >
            Last name
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => insertSubjectValue(smartCodeMap?.contactEmail)}
            disabled={!smartCodeMap?.contactEmail}
          >
            Contact email
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => insertSubjectValue(smartCodeMap?.docId)}
            disabled={!smartCodeMap?.docId}
          >
            Doc ID
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => insertSubjectValue(`Presentation #${smartCodeMap?.docId} from Elite Core`)}
            disabled={!smartCodeMap?.docId}
          >
            Presentation subject
          </Button>
        </Stack>
      )}

      {showLayout && (
        <TextField
          label="Email layout"
          select
          size="small"
          value={safeValue.layout || 'plain'}
          onChange={(event) => updateValue({ layout: event.target.value })}
          helperText="Choose how the email is wrapped before sending."
        >
          {defaultLayoutOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Email body
          </Typography>
          <Stack direction="row" spacing={1}>
            <Box
              component="button"
              type="button"
              onClick={() => setBodyMode('visual')}
              style={{
                padding: '4px 10px',
                borderRadius: 6,
                border: bodyMode === 'visual' ? '1px solid #1976d2' : '1px solid #d0d7de',
                background: bodyMode === 'visual' ? '#e3f2fd' : '#fff',
                cursor: 'pointer',
              }}
            >
              Visual
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => setBodyMode('html')}
              style={{
                padding: '4px 10px',
                borderRadius: 6,
                border: bodyMode === 'html' ? '1px solid #1976d2' : '1px solid #d0d7de',
                background: bodyMode === 'html' ? '#e3f2fd' : '#fff',
                cursor: 'pointer',
              }}
            >
              HTML
            </Box>
          </Stack>
        </Box>

        {bodyMode === 'visual' ? (
          <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" size="small" onClick={() => applyBodyCommand('bold')}>
                Bold
              </Button>
              <Button variant="outlined" size="small" onClick={() => applyBodyCommand('italic')}>
                Italic
              </Button>
              <Button variant="outlined" size="small" onClick={() => applyBodyCommand('underline')}>
                Underline
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const url = window.prompt('Enter a URL');
                  if (url) applyBodyCommand('createLink', url);
                }}
              >
                Link
              </Button>
              <Button variant="outlined" size="small" onClick={() => applyBodyCommand('insertUnorderedList')}>
                Bullet list
              </Button>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Button
                variant="outlined"
                size="small"
                onClick={() => insertSmartCodeValue('contactFirstName')}
                disabled={!smartCodeMap?.contactFirstName}
              >
                First name
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => insertSmartCodeValue('contactLastName')}
                disabled={!smartCodeMap?.contactLastName}
              >
                Last name
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => insertSmartCodeValue('contactEmail')}
                disabled={!smartCodeMap?.contactEmail}
              >
                Contact email
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => insertSmartCodeValue('docId')}
                disabled={!smartCodeMap?.docId}
              >
                Doc ID
              </Button>
            </Stack>
            <Box
              ref={visualBodyRef}
              contentEditable
              suppressContentEditableWarning
              onInput={(event) => updateValue({ body: event.currentTarget.innerHTML })}
              style={{
                minHeight: 160,
                border: '1px solid #d0d7de',
                borderRadius: 6,
                padding: 12,
                fontFamily: 'inherit',
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Visual editor outputs HTML.
            </Typography>
          </Box>
        ) : (
          <TextField
            multiline
            minRows={6}
            value={safeValue.body}
            onChange={(event) => updateValue({ body: event.target.value })}
            helperText="HTML + smart codes supported: {contactFirstName}, {contactLastName}, {contactEmail}, {docId}"
          />
        )}

        <Box sx={{ mt: 2 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setShowPreview((prev) => !prev)}
            sx={{ px: 0, minWidth: 0, alignSelf: 'flex-start' }}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
          {showPreview && (
            <EmailPreviewPanel
              bodyHtml={previewBodyHtml ?? safeValue.body}
              layout={safeValue.layout || 'plain'}
              subject={safeValue.subject}
              fromName={fromName}
              fromEmail={fromEmail}
              showTemplatePreviewRow={showTemplatePreviewRow}
              to={normalizeContactList(safeValue.to)}
              cc={normalizeContactList(safeValue.cc)}
              bcc={normalizeContactList(safeValue.bcc)}
              attachments={attachments}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EmailComposer;
